import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError, apiClient } from '@/modules/api/api-client';
import { INFREQUENT_STALE_TIME } from '@/modules/query/query-client';
import { queryKeys } from '@/modules/query/query-keys';
import { validateBranches, validatePRDescription, validatePRTitle } from '@/utils/validation';

/** Branch info from GET /repos/:name/branches */
interface BranchInfo {
  name: string;
  commitId: string;
}

/** Paginated response from backend */
interface PaginatedResponse<T> {
  items: T[];
  cursor?: string;
}

/** Repository detail for getting default branch */
interface RepositoryDetail {
  name: string;
  defaultBranch?: string;
}

/** Response from POST /repos/:name/prs */
interface CreatePrResponse {
  prId: string;
}

/** Commit summary from branch comparison */
interface CommitSummary {
  commitId: string;
  message: string;
  authorName: string;
  authorDate: string;
}

interface FormErrors {
  title?: string;
  description?: string;
  branches?: string;
  form?: string;
}

export function CreatePrPage() {
  const { name, repoName: repoNameParam, projectKey } = useParams<{
    name: string;
    repoName: string;
    projectKey: string;
  }>();
  const repoName = repoNameParam ?? name ?? '';
  const navigate = useNavigate();
  const repoPath = projectKey
    ? `/projects/${encodeURIComponent(projectKey)}/repos/${encodeURIComponent(repoName)}`
    : `/repos/${encodeURIComponent(repoName)}`;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sourceBranch, setSourceBranch] = useState('');
  const [destinationBranch, setDestinationBranch] = useState('');
  const [aiReviewEnabled, setAiReviewEnabled] = useState(false);
  // Tracks whether the user has explicitly chosen a destination branch. Once true, the
  // repo default-branch pre-fill must never override the user's selection. The default
  // branch is only a convenience seed for the dropdown; it does not constrain source or
  // destination selection.
  const [destinationTouched, setDestinationTouched] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Fetch repository detail to get default branch
  const { data: repoDetail } = useQuery({
    queryKey: queryKeys.repos.detail(repoName),
    queryFn: () => apiClient.get<RepositoryDetail>(`/repos/${encodeURIComponent(repoName)}`),
    staleTime: INFREQUENT_STALE_TIME,
    enabled: !!repoName,
  });

  // Fetch branches
  const {
    data: branchesData,
    isLoading: branchesLoading,
    isError: branchesError,
    refetch: refetchBranches,
  } = useQuery({
    queryKey: queryKeys.repos.branches(repoName),
    queryFn: () =>
      apiClient.get<PaginatedResponse<BranchInfo>>(
        `/repos/${encodeURIComponent(repoName)}/branches`,
      ),
    staleTime: INFREQUENT_STALE_TIME,
    enabled: !!repoName,
  });

  // Seed the destination dropdown with the repository's default branch as a one-time
  // convenience, ONLY if the user has not already chosen a destination. Runs in an effect
  // (never during render) so it cannot race the user's selection or leak a stale value into
  // the commit-preview query. The default branch is inconsequential to what the user
  // ultimately selects for source or destination.
  const defaultBranch = repoDetail?.defaultBranch ?? '';
  useEffect(() => {
    if (destinationTouched || destinationBranch || !defaultBranch || !branchesData) {
      return;
    }
    const branchExists = branchesData.items.some((b) => b.name === defaultBranch);
    if (branchExists) {
      setDestinationBranch(defaultBranch);
    }
  }, [branchesData, defaultBranch, destinationBranch, destinationTouched]);

  // Fetch commits between branches to pre-populate description
  const { data: commitsData } = useQuery<CommitSummary[]>({
    queryKey: ['repos', repoName, 'branch-commits', sourceBranch, destinationBranch],
    queryFn: () =>
      apiClient.get<CommitSummary[]>(
        `/repos/${encodeURIComponent(repoName)}/commits?sourceBranch=${encodeURIComponent(sourceBranch)}&destinationBranch=${encodeURIComponent(destinationBranch)}`,
      ),
    staleTime: INFREQUENT_STALE_TIME,
    enabled: !!repoName && !!sourceBranch && !!destinationBranch && sourceBranch !== destinationBranch,
  });

  // Pre-populate description with commit messages when commits load (only if description is empty)
  const [descriptionPrePopulated, setDescriptionPrePopulated] = useState(false);
  useEffect(() => {
    if (commitsData && commitsData.length > 0 && !descriptionPrePopulated && !description) {
      const commitLines = commitsData.map((c) => `- ${c.message.split('\n')[0]}`).join('\n');
      setDescription(commitLines);
      setDescriptionPrePopulated(true);
    }
  }, [commitsData, descriptionPrePopulated, description]);

  // Submit mutation
  const createPrMutation = useMutation({
    mutationFn: (payload: {
      title: string;
      description: string;
      sourceBranch: string;
      destinationBranch: string;
      aiReviewEnabled: boolean;
    }) =>
      apiClient.post<CreatePrResponse>(
        `/repos/${encodeURIComponent(repoName)}/prs`,
        payload,
      ),
    onSuccess: (data) => {
      navigate(`${repoPath}/prs/${data.prId}`);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 400) {
        const body = error.body as { message?: string } | undefined;
        setErrors((prev) => ({
          ...prev,
          form: body?.message ?? 'Invalid request. Please check your inputs.',
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          form: 'An unexpected error occurred. Please try again.',
        }));
      }
    },
  });

  function validateForm(): boolean {
    const newErrors: FormErrors = {};

    const titleResult = validatePRTitle(title);
    if (!titleResult.valid) {
      newErrors.title = titleResult.error;
    }

    const descResult = validatePRDescription(description);
    if (!descResult.valid) {
      newErrors.description = descResult.error;
    }

    const branchResult = validateBranches(sourceBranch, destinationBranch);
    if (!branchResult.valid) {
      newErrors.branches = branchResult.error;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    createPrMutation.mutate({
      title: title.trim(),
      description,
      sourceBranch,
      destinationBranch,
      aiReviewEnabled,
    });
  }

  const isSubmitting = createPrMutation.isPending;
  const branches = branchesData?.items ?? [];

  return (
    <main id="main-content" className="max-w-3xl mx-auto px-4 py-6">
      <nav aria-label="Breadcrumb" className="mb-4">
        <ol className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
          <li>
            <Link
              to={repoPath}
              className="hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            >
              {repoName}
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="text-gray-900 dark:text-gray-100">
            New Pull Request
          </li>
        </ol>
      </nav>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        Create Pull Request
      </h1>

      {/* Branch loading error */}
      {branchesError && (
        <div
          role="alert"
          className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
        >
          <p className="font-medium">Failed to load branches</p>
          <p className="text-sm mt-1">Branches could not be loaded from the repository.</p>
          <button
            type="button"
            onClick={() => refetchBranches()}
            className="mt-2 text-sm font-medium text-red-700 dark:text-red-300 underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
          >
            Retry
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Form-level error (from API 400 responses) */}
        {errors.form && (
          <div
            role="alert"
            className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
          >
            <p className="text-sm">{errors.form}</p>
          </div>
        )}

        {/* Title field */}
        <div className="mb-5">
          <label
            htmlFor="pr-title"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Title <span aria-hidden="true">*</span>
          </label>
          <input
            id="pr-title"
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (errors.title) {
                setErrors((prev) => ({ ...prev, title: undefined }));
              }
            }}
            maxLength={256}
            required
            aria-required="true"
            aria-invalid={!!errors.title}
            aria-describedby={errors.title ? 'pr-title-error' : undefined}
            className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.title
                ? 'border-red-500 dark:border-red-400'
                : 'border-gray-300 dark:border-gray-700'
            }`}
            placeholder="Enter a descriptive title for your pull request"
          />
          {errors.title && (
            <p id="pr-title-error" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {errors.title}
            </p>
          )}
        </div>

        {/* Description field */}
        <div className="mb-5">
          <label
            htmlFor="pr-description"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Description
          </label>
          <textarea
            id="pr-description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (errors.description) {
                setErrors((prev) => ({ ...prev, description: undefined }));
              }
            }}
            maxLength={10240}
            rows={6}
            aria-invalid={!!errors.description}
            aria-describedby={errors.description ? 'pr-description-error' : undefined}
            className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.description
                ? 'border-red-500 dark:border-red-400'
                : 'border-gray-300 dark:border-gray-700'
            }`}
            placeholder="Describe the changes in this pull request (optional)"
          />
          {errors.description && (
            <p id="pr-description-error" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {errors.description}
            </p>
          )}
        </div>

        {/* Branch selectors */}
        <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Source branch */}
          <div>
            <label
              htmlFor="source-branch"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Source branch <span aria-hidden="true">*</span>
            </label>
            <select
              id="source-branch"
              value={sourceBranch}
              onChange={(e) => {
                setSourceBranch(e.target.value);
                setDescriptionPrePopulated(false);
                setDescription('');
                if (errors.branches) {
                  setErrors((prev) => ({ ...prev, branches: undefined }));
                }
              }}
              required
              aria-required="true"
              aria-invalid={!!errors.branches}
              aria-describedby={errors.branches ? 'branches-error' : undefined}
              disabled={branchesLoading || branchesError}
              className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                errors.branches
                  ? 'border-red-500 dark:border-red-400'
                  : 'border-gray-300 dark:border-gray-700'
              }`}
            >
              <option value="">Select source branch</option>
              {branches.map((branch) => (
                <option key={branch.name} value={branch.name}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>

          {/* Destination branch */}
          <div>
            <label
              htmlFor="destination-branch"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Destination branch <span aria-hidden="true">*</span>
            </label>
            <select
              id="destination-branch"
              value={destinationBranch}
              onChange={(e) => {
                setDestinationTouched(true);
                setDestinationBranch(e.target.value);
                setDescriptionPrePopulated(false);
                setDescription('');
                if (errors.branches) {
                  setErrors((prev) => ({ ...prev, branches: undefined }));
                }
              }}
              required
              aria-required="true"
              aria-invalid={!!errors.branches}
              aria-describedby={errors.branches ? 'branches-error' : undefined}
              disabled={branchesLoading || branchesError}
              className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                errors.branches
                  ? 'border-red-500 dark:border-red-400'
                  : 'border-gray-300 dark:border-gray-700'
              }`}
            >
              <option value="">Select destination branch</option>
              {branches.map((branch) => (
                <option key={branch.name} value={branch.name}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Branch validation error */}
        {errors.branches && (
          <p id="branches-error" className="mb-5 -mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
            {errors.branches}
          </p>
        )}

        {/* AI review toggle */}
        <div className="mb-5 rounded-lg border border-violet-200 bg-violet-50/50 p-4 dark:border-violet-800 dark:bg-violet-900/10">
          <label className="flex cursor-pointer items-start gap-3" htmlFor="review-with-ai">
            <input
              id="review-with-ai"
              type="checkbox"
              checked={aiReviewEnabled}
              onChange={(e) => setAiReviewEnabled(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500 dark:border-gray-600 dark:bg-gray-900"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                Review with AI
              </span>
              <span className="mt-1 block text-xs text-gray-600 dark:text-gray-400">
                Run an AI review for this pull request after it is created.
              </span>
            </span>
          </label>
        </div>

        {/* Submit button */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={isSubmitting || branchesLoading || branchesError}
            className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
          >
            {isSubmitting && (
              <svg
                className="mr-2 h-4 w-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isSubmitting ? 'Creating...' : 'Create Pull Request'}
          </button>
            <Link
              to={repoPath}
              className="rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
            Cancel
          </Link>
        </div>
      </form>
    </main>
  );
}
